import { FC, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useGetAccounts, useCreateAccount } from "../../hooks/useAccounts";
import { AccountRequest } from "../../domain/account.interface";

const AccountsComponent: FC = () => {
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [formData, setFormData] = useState<AccountRequest>({
		name: "",
		status: "ACTIVE",
		description: "",
		auth0: {
			audience: "",
			clientIdSPA: "",
			connection: "Username-Password-Authentication",
			connectionId: "",
			orgId: "",
			ssoRedirectUrl: "",
		},
		qlik: {
			clientId: "",
			clientSecret: "",
			userId: "",
			username: "",
			applicationId: "",
		},
		tremendous: {
			apiKey: "",
			url: "https://api.tremendous.com",
		},
	});

	const { data: accounts, isLoading, error, refetch } = useGetAccounts();
	const createMutation = useCreateAccount();

	const handleCreateAccount = async () => {
		try {
			await createMutation.mutateAsync(formData);
			setShowCreateForm(false);
			setFormData({
				name: "",
				status: "ACTIVE",
				description: "",
				auth0: {
					audience: "",
					clientIdSPA: "",
					connection: "Username-Password-Authentication",
					connectionId: "",
					orgId: "",
					ssoRedirectUrl: "",
				},
				qlik: {
					clientId: "",
					clientSecret: "",
					userId: "",
					username: "",
					applicationId: "",
				},
				tremendous: {
					apiKey: "",
					url: "https://api.tremendous.com",
				},
			});
			refetch();
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error creating account:", error);
		}
	};

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({ ...prev, [field]: value }));
	};

	const handleNestedInputChange = (
		section: "auth0" | "qlik" | "tremendous",
		field: string,
		value: string,
	) => {
		setFormData((prev) => ({
			...prev,
			[section]: { ...prev[section], [field]: value },
		}));
	};

	if (isLoading) return <Typography>Loading accounts...</Typography>;
	if (error)
		return <Typography color="error">Error loading accounts</Typography>;

	return (
		<Box sx={{ padding: 3 }}>
			<Box
				display="flex"
				justifyContent="space-between"
				alignItems="center"
				mb={3}
			>
				<Typography variant="h4" component="h1">
					Account Management
				</Typography>
				<Button
					variant="contained"
					onClick={() => setShowCreateForm(!showCreateForm)}
				>
					{showCreateForm ? "Cancel" : "Create New Account"}
				</Button>
			</Box>

			{showCreateForm && (
				<Paper sx={{ padding: 3, marginBottom: 3 }}>
					<Typography variant="h6" gutterBottom>
						Create New Account
					</Typography>
					<Grid container spacing={3}>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Account Name"
								value={formData.name}
								onChange={(e) => handleInputChange("name", e.target.value)}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<FormControl fullWidth>
								<InputLabel>Status</InputLabel>
								<Select
									value={formData.status}
									label="Status"
									onChange={(e) => handleInputChange("status", e.target.value)}
								>
									<MenuItem value="ACTIVE">Active</MenuItem>
									<MenuItem value="INACTIVE">Inactive</MenuItem>
								</Select>
							</FormControl>
						</Grid>
						<Grid item xs={12}>
							<TextField
								fullWidth
								label="Description"
								value={formData.description}
								onChange={(e) =>
									handleInputChange("description", e.target.value)
								}
								multiline
								rows={2}
							/>
						</Grid>

						{/* Auth0 Configuration */}
						<Grid item xs={12}>
							<Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
								Auth0 Configuration
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Audience"
								value={formData.auth0.audience}
								onChange={(e) =>
									handleNestedInputChange("auth0", "audience", e.target.value)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Client ID SPA"
								value={formData.auth0.clientIdSPA}
								onChange={(e) =>
									handleNestedInputChange(
										"auth0",
										"clientIdSPA",
										e.target.value,
									)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Connection"
								value={formData.auth0.connection}
								onChange={(e) =>
									handleNestedInputChange("auth0", "connection", e.target.value)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Connection ID"
								value={formData.auth0.connectionId}
								onChange={(e) =>
									handleNestedInputChange(
										"auth0",
										"connectionId",
										e.target.value,
									)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Organization ID"
								value={formData.auth0.orgId}
								onChange={(e) =>
									handleNestedInputChange("auth0", "orgId", e.target.value)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="SSO Redirect URL"
								value={formData.auth0.ssoRedirectUrl}
								onChange={(e) =>
									handleNestedInputChange(
										"auth0",
										"ssoRedirectUrl",
										e.target.value,
									)
								}
								required
							/>
						</Grid>

						{/* Qlik Configuration */}
						<Grid item xs={12}>
							<Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
								Qlik Configuration
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Client ID"
								value={formData.qlik.clientId}
								onChange={(e) =>
									handleNestedInputChange("qlik", "clientId", e.target.value)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Client Secret"
								type="password"
								value={formData.qlik.clientSecret}
								onChange={(e) =>
									handleNestedInputChange(
										"qlik",
										"clientSecret",
										e.target.value,
									)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="User ID"
								value={formData.qlik.userId}
								onChange={(e) =>
									handleNestedInputChange("qlik", "userId", e.target.value)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Username"
								value={formData.qlik.username}
								onChange={(e) =>
									handleNestedInputChange("qlik", "username", e.target.value)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="Application ID"
								value={formData.qlik.applicationId}
								onChange={(e) =>
									handleNestedInputChange(
										"qlik",
										"applicationId",
										e.target.value,
									)
								}
								required
							/>
						</Grid>

						{/* Tremendous Configuration */}
						<Grid item xs={12}>
							<Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
								Tremendous Configuration
							</Typography>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="API Key"
								type="password"
								value={formData.tremendous.apiKey}
								onChange={(e) =>
									handleNestedInputChange(
										"tremendous",
										"apiKey",
										e.target.value,
									)
								}
								required
							/>
						</Grid>
						<Grid item xs={12} md={6}>
							<TextField
								fullWidth
								label="URL"
								value={formData.tremendous.url}
								onChange={(e) =>
									handleNestedInputChange("tremendous", "url", e.target.value)
								}
								required
							/>
						</Grid>

						<Grid item xs={12}>
							<Box display="flex" gap={2} justifyContent="flex-end" mt={2}>
								<Button
									variant="outlined"
									onClick={() => setShowCreateForm(false)}
								>
									Cancel
								</Button>
								<Button
									variant="contained"
									onClick={handleCreateAccount}
									disabled={createMutation.isPending}
								>
									{createMutation.isPending ? "Creating..." : "Create Account"}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Paper>
			)}

			<Grid container spacing={3}>
				{accounts?.map((account) => (
					<Grid item xs={12} md={6} lg={4} key={account._id}>
						<Card>
							<CardContent>
								<Box
									display="flex"
									justifyContent="space-between"
									alignItems="center"
									mb={2}
								>
									<Typography variant="h6">{account.name}</Typography>
									<Chip
										label={account.status}
										color={account.status === "ACTIVE" ? "success" : "default"}
									/>
								</Box>
								<Typography variant="body2" color="text.secondary" paragraph>
									{account.description || "No description"}
								</Typography>
								<Typography variant="caption" display="block" gutterBottom>
									Created:{" "}
									{new Date(account.createdAt || "").toLocaleDateString()}
								</Typography>
								<Typography variant="caption" display="block">
									ID: {account._id}
								</Typography>
							</CardContent>
						</Card>
					</Grid>
				))}
			</Grid>
		</Box>
	);
};

export default AccountsComponent;
